import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import type { Workspace } from "@/hooks/useWorkspace";
import { useWorkspaceMembers, type MemberRow } from "./workspaceMembers/useWorkspaceMembers";
import { InviteMemberForm } from "./workspaceMembers/InviteMemberForm";
import { PendingInvitesList } from "./workspaceMembers/PendingInvitesList";
import { MembersList } from "./workspaceMembers/MembersList";
import { TransferOwnershipDialog } from "./workspaceMembers/TransferOwnershipDialog";

type Props = { workspace: Workspace };

/**
 * Agency-only team management card. Shown inside per-workspace Settings tab.
 * Lets the workspace owner invite teammates by email, view current members,
 * change their role, resend or revoke pending invites, and revoke access.
 */
export const WorkspaceMembersCard = ({ workspace }: Props) => {
  const s = useWorkspaceMembers(workspace);
  const [transferTarget, setTransferTarget] = useState<MemberRow | null>(null);

  const activeMembers = useMemo(() => s.members.filter((m) => m.status !== "revoked"), [s.members]);
  const revokedMembers = useMemo(() => s.members.filter((m) => m.status === "revoked"), [s.members]);

  if (!s.visible) return null;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" /> Team members
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Invite teammates by email and manage their access to this workspace.
        </p>

        <InviteMemberForm
          onSubmit={s.sendInvite}
          accountSeatsMax={s.accountSeatsMax}
          accountSeatUsage={s.accountSeatUsage}
          perWorkspaceMax={s.perWorkspaceMax}
          workspaceSeatUsage={s.workspaceSeatUsage}
          seatsAvailable={s.seatsAvailable}
        />

        <PendingInvitesList
          invites={s.invites}
          busyId={s.busyId}
          onCopy={s.copyInviteLink}
          onResend={s.resendInvite}
          onRevoke={s.revokeInvite}
        />

        <MembersList
          workspace={workspace}
          loading={s.loading}
          activeMembers={activeMembers}
          revokedMembers={revokedMembers}
          displayMap={s.displayMap}
          busyId={s.busyId}
          currentUserId={s.user?.id}
          onUpdateRole={s.updateRole}
          onRevoke={s.revoke}
          onRequestTransfer={(m) => setTransferTarget(m)}
        />
      </CardContent>

      <TransferOwnershipDialog
        workspace={workspace}
        target={transferTarget}
        displayMap={s.displayMap}
        onClose={() => setTransferTarget(null)}
        onConfirm={s.transferOwnership}
      />
    </Card>
  );
};
